import {
  ApolloClient,
  ApolloLink,
  createQueryPreloader,
  HttpLink,
  PreloadQueryFunction,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { ConnectionStatusType } from '@/components/connection-status/connection-status-type.ts';
import { websocketStatusVar } from '@/components/websocket-status-var.ts';
import { traceError } from '@/utils/trace-error.ts';
import { AWS_GRAPHQL2_API_URL } from '../env.ts';
import { getAuthorizedData } from '../routes-utils/handle-authorized-data.ts';
import { makeApolloCache } from './apollo-cache.ts';
import { getApolloClient } from './apollo-client-instance.ts';
import { shouldReportGraphQLError } from './graphql-error-reporting.ts';

const url = AWS_GRAPHQL2_API_URL;

const httpLink = new HttpLink({
  uri: (operation) => `${url}?${operation.operationName}`,
});

const urlRealtime = AWS_GRAPHQL2_API_URL.replace(`http`, `ws`) + '/realtime';

const wsLink = new GraphQLWsLink(
  createClient({
    url: urlRealtime,
    connectionParams: async () => {
      const { tokens } = await getAuthorizedData();

      return {
        authorization: tokens?.accessToken.toString()
          ? `Cognito ${tokens.accessToken}`
          : '',
      };
    },
    shouldRetry: () => true,
    retryAttempts: 30,
    keepAlive: 30000,
    on: {
      connected: () => {
        websocketStatusVar(ConnectionStatusType.Connected);
      },
      closed: () => {
        websocketStatusVar(ConnectionStatusType.Disconnected);
      },
      connecting: () => {
        websocketStatusVar(ConnectionStatusType.Connecting);
      },
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const authLink = setContext(async (_, { headers }) => {
  const { tokens } = await getAuthorizedData();

  if (tokens) {
    return {
      headers: {
        ...headers,
        Authorization: `Cognito ${tokens.accessToken}`,
      },
    };
  }

  return {
    headers: headers,
  };
});

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, path, extensions }) => {
      if (!shouldReportGraphQLError({ extensions, path })) return;

      traceError(new Error('[GraphQL error]: ' + message));
    });
  if (networkError) {
    if (networkError.name === 'AbortError') {
      return;
    }

    traceError(
      new Error('[GraphQL network error]: ' + networkError.message, {
        cause: networkError,
      }),
    );
  }
});

export const makeApolloClient = () => {
  return new ApolloClient({
    cache: makeApolloCache(),
    link: ApolloLink.from([errorLink, authLink, splitLink]),
  });
};

let queryPreloader: PreloadQueryFunction;

export const preloadQuery: PreloadQueryFunction = (
  ...args: Parameters<PreloadQueryFunction>
) => {
  queryPreloader = queryPreloader || createQueryPreloader(getApolloClient());
  return queryPreloader(...args);
};
